/**
 * The brand page as a route module (web plan W4.4): `/brand/:ref` and, under
 * each launched locale's layout, `/{cc}/{lang}/brand/:ref` (src/routes.ts).
 *
 * The server loader reads the SEO server's answer for the URL (status,
 * redirect, head and the brand's page data; @/seo/entityDocument), so the
 * document's body is the real page, not a loading state. In-app navigations
 * load through Apollo (`clientLoader`, @/lib/entityClientLoader).
 */
import type { ClientLoaderFunctionArgs, LoaderFunctionArgs } from 'react-router';
import { EntityRoute } from '@/components/seo/EntityRoute';
import { RouteErrorBoundary } from '@/components/seo/RouteErrorBoundary';
import { entityClientLoader, entityShouldRevalidate } from '@/lib/entityClientLoader';
import BrandDetailPage from '@/pages/detail/BrandDetailPage';
import { entityHeaders, entityMeta, loadEntityDocument } from '@/seo/entityDocument';

export function loader({ context }: LoaderFunctionArgs) {
  return loadEntityDocument('brand', context);
}

const loadInBrowser = entityClientLoader('brand');

export function clientLoader(args: ClientLoaderFunctionArgs) {
  return loadInBrowser(args);
}

/** The document uses the server loader's data; only in-app navigations run `clientLoader`. */
clientLoader.hydrate = false as const;

/** A query-string-only change keeps the loaded entity. */
export const shouldRevalidate = entityShouldRevalidate;

export const headers = entityHeaders;

export const meta = entityMeta;

export const ErrorBoundary = RouteErrorBoundary;

export default function BrandRoute() {
  return (
    <EntityRoute>
      <BrandDetailPage />
    </EntityRoute>
  );
}
